import React, { useState, useEffect } from 'react';
import {
    Container,
    TextField,
    Button,
    Grid,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function AdminPanel() {
    const [bookings, setBookings] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [loggedIn, setLoggedIn] = useState(() => {
        return localStorage.getItem('isLoggedIn') === 'true';
    });
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [adminId, setAdminId] = useState(() => {
        return localStorage.getItem('adminId') || '';
    });
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [language, setLanguage] = useState('ar');
    const [searchTerm, setSearchTerm] = useState(""); // State for search term
    const [filteredBookings, setFilteredBookings] = useState(bookings); // State for filtered bookings

    // Filter bookings based on the search term
    useEffect(() => {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        const filtered = bookings.filter(booking =>
            Object.values(booking).some(value =>
                value && value.toString().toLowerCase().includes(lowercasedSearchTerm)
            )
        );
        setFilteredBookings(filtered);
    }, [searchTerm, bookings]);

    const serviceTypeOptions = [
        // Makeup Options
        { value: 'brideMakeup', label: language === 'en' ? 'Makeup (Bride)' : 'ميك أب (عروس)' },
        { value: 'receptionMakeup', label: language === 'en' ? 'Makeup (Reception)' : 'ميك اب (استقبال)' },
        { value: 'engagementMakeup', label: language === 'en' ? 'Makeup (Engagement)' : 'ميك اب (خطبه)' },
        { value: 'milgaMakeup', label: language === 'en' ? 'Makeup (Milga)' : 'ميك اب (ملجه)' },
        { value: 'yellowMakeup', label: language === 'en' ? 'Makeup (Yellow)' : 'ميك اب (يلوه)' },
        { value: 'eveningMakeup', label: language === 'en' ? 'Makeup (Evening)' : 'ميك اب (مسائيه)' },
        { value: 'partyMakeup', label: language === 'en' ? 'Makeup (Party)' : 'ميك أب (سهره)' },

        // Hairstyle Options
        { value: 'brideHairstyle', label: language === 'en' ? 'Hairstyle (Bride)' : 'تسريحه (عروس)' },
        { value: 'receptionHairstyle', label: language === 'en' ? 'Hairstyle (Reception)' : 'تسريحه (استقبال)' },
        { value: 'milgaHairstyle', label: language === 'en' ? 'Hairstyle (Milga)' : 'تسريحه (ملجه)' },
        { value: 'engagementHairstyle', label: language === 'en' ? 'Hairstyle (Engagement)' : 'تسريحه (خطبه)' },
        { value: 'yellowHairstyle', label: language === 'en' ? 'Hairstyle (Yellow)' : 'تسريحه (يلوه)' },
        { value: 'partyHairstyle', label: language === 'en' ? 'Hairstyle (Party)' : 'تسريحه (سهره)' },

        // Hair Treatments
        { value: 'extension', label: language === 'en' ? 'Extension' : 'تركيب اكستنشن' },
        { value: 'blowDry', label: language === 'en' ? 'Blow Dry' : 'سشوار' },
        { value: 'wavy', label: language === 'en' ? 'Wavy' : 'ويفي' },
        { value: 'hairRegrowth', label: language === 'en' ? 'Hair Treatment (Regrowth)' : 'علاجات شعر (انبات)' },
        { value: 'hairFallTreatment', label: language === 'en' ? 'Hair Treatment (Anti-hair fall)' : 'علاجات شعر (علاج التساقط)' },
        { value: 'olaplex', label: language === 'en' ? 'Olaplex Treatment' : 'علاجات شعر (اولابلكس)' },
        { value: 'agonMask', label: language === 'en' ? 'Agon Mask Treatment' : 'علاجات شعر(اجون ماسك)' },
        { value: 'tia', label: language === 'en' ? 'Tia Treatment' : 'علاجات شعر(تيا)' },
        { value: 'charcoalMask', label: language === 'en' ? 'Charcoal Mask' : 'علاجات شعر(ماسك الفحم)' },
        { value: 'caviarMask', label: language === 'en' ? 'Caviar Mask' : 'علاجات شعر(ماسك كافيار)' },
        { value: 'bbPlus', label: language === 'en' ? 'BB Plus Treatment' : 'علاجات شعر(+bb)' },
        { value: 'amazon', label: language === 'en' ? 'Amazon Treatment' : 'علاجات شعر(امازون)' },
        { value: 'omega3', label: language === 'en' ? 'Omega 3 Treatment' : 'علاجات شعر(اوميجا 3)' },
        { value: 'protein', label: language === 'en' ? 'Protein Treatment' : 'علاجات شعر(بروتين)' },
        { value: 'filler', label: language === 'en' ? 'Hair Filler Treatment' : 'علاجات شعر(فلر)' },

        // Skin and Beauty Services
        { value: 'skinTreatment', label: language === 'en' ? 'Skin Cleaning' : 'تنظيف بشره' },
        { value: 'lashExtensions', label: language === 'en' ? 'Lash Extensions' : 'تركيب رموش' },
        { value: 'turkishStyle', label: language === 'en' ? 'Turkish Style' : 'تركش ستايل' },
        { value: 'lashLift', label: language === 'en' ? 'Lash Lift' : 'رفع الرموش' },
        { value: 'browCleaning', label: language === 'en' ? 'Brow Cleaning' : 'تنظيف الحواجب' },
        { value: 'semiPermanentMakeup', label: language === 'en' ? 'Semi-Permanent Makeup (Microblading)' : 'المكياج شبه الدائم (المايكروبليدنج)' },
        { value: 'faceShaving', label: language === 'en' ? 'Full Face Shaving' : 'حف وجه كامل' },
        { value: 'waxing', label: language === 'en' ? 'Waxing' : 'شمع' },
        { value: 'moroccanBath', label: language === 'en' ? 'Moroccan Bath' : 'حمام مغربي' },
        { value: 'turkishBath', label: language === 'en' ? 'Turkish Bath' : 'حمام تركي' },

        // Massage Options
        { value: 'fullBodyMassage', label: language === 'en' ? 'Full Body Massage' : 'مساج (فل بودي)' },
        { value: 'footMassage', label: language === 'en' ? 'Foot Massage' : 'مساج (القدمين)' },
        { value: 'hotStoneMassage', label: language === 'en' ? 'Hot Stone Massage' : 'مساج (حجرحراري)' },
        { value: 'neckMassage', label: language === 'en' ? 'Neck Massage' : 'مساج (العنق)' },
        { value: 'headMassage', label: language === 'en' ? 'Head Massage' : 'مساج (الرأس)' },
        { value: 'relaxationMassage', label: language === 'en' ? 'Relaxation Massage' : 'مساج (ريلكسيشن)' },

        // Nail and Foot Care Options
        { value: 'pedicure', label: language === 'en' ? 'Pedicure' : 'بديكير' },
        { value: 'manicure', label: language === 'en' ? 'Manicure' : 'مانيكير' },
        { value: 'footlogix', label: language === 'en' ? 'Footlogix' : 'فوت لوجكس' },
        { value: 'handGloves', label: language === 'en' ? 'Hand Gloves' : 'قفازات اليد' },
        { value: 'footGloves', label: language === 'en' ? 'Foot Gloves' : 'قفازات القدم' },
        { value: 'acrylic', label: language === 'en' ? 'Acrylic' : 'اكريليك' },
        { value: 'frenchAcrylic', label: language === 'en' ? 'French Acrylic' : 'اكريليك فرنسي' },
        { value: 'polygel', label: language === 'en' ? 'Poly Gel' : 'بولي جل' },
        { value: 'gelPolish', label: language === 'en' ? 'Gel Polish' : 'طلاء الجلش' },
        { value: 'nailPolish', label: language === 'en' ? 'Nail Polish' : 'طلاء الاضافر' },
        { value: 'ombrePolish', label: language === 'en' ? 'Ombre Polish' : 'طلاء الاومبري' },
    ];

    const assignedStaffOptions = [
        { value: 'mariamKazem', label: language === 'en' ? 'Makeup Artist & Hairstylist Mariam Kazem' : 'الميكب ارتست والهيرستايل مريم كاظم' },
        { value: 'deema', label: language === 'en' ? 'Makeup Artist Deema' : 'الميكب ارتست ديمة' },
        { value: 'alam', label: language === 'en' ? 'Hairstylist Alam' : 'الهيرستايل عالم' },
        { value: 'khadija', label: language === 'en' ? 'Hairstylist Khadija' : 'الهيرستايل خديجه' },
        { value: 'farah', label: language === 'en' ? 'Hairstylist Farah' : 'الهيرستايل فرح' },
        { value: 'fawzia', label: language === 'en' ? 'Assistant Fawzia' : 'المساعدة فوزيه' },
        { value: 'debal', label: language === 'en' ? 'Assistant Debal' : 'المساعدة ديبال' },
    ];

    const partyLocationOptions = [
        { value: '', label: language === 'en' ? 'Select Party Location' : 'اختر مكان الحفلة' },
        { value: 'hotel', label: language === 'en' ? 'Hotel' : 'فندق' },
        { value: 'house', label: language === 'en' ? 'House' : 'بيت' },
        { value: 'weddingHall', label: language === 'en' ? 'Wedding Hall' : 'صالة الافراح' },
        { value: 'hall', label: language === 'en' ? 'Hall' : 'قاعة' },
        { value: 'lounge', label: language === 'en' ? 'Lounge' : 'لاونش' },
        { value: 'chalet', label: language === 'en' ? 'Chalet' : 'شاليه' },
        { value: 'farm', label: language === 'en' ? 'Farm' : 'مزرعة' },
        { value: 'makeupSalon', label: language === 'en' ? 'Makeup Artist Salon' : 'صالون ميكب ارتست' },
        { value: 'beautyInstitute', label: language === 'en' ? 'Beauty Institute and Salon' : 'معهد و صالون بيس' },
        { value: 'photoStudio', label: language === 'en' ? 'Photography Studio' : 'ستوديو التصوير' },
        { value: 'restaurant', label: language === 'en' ? 'Restaurant' : 'مطعم' },
        { value: 'gccCountries', label: language === 'en' ? 'GCC Countries' : 'دول مجلس التعاون' },
        { value: 'toBeDecided', label: language === 'en' ? 'To be decided later' : 'سوف يتم تحديده لاحقاً' },
    ];

    const hotelOptions = [
        { value: '', label: language === 'en' ? 'Select Hotel Name' : 'اختر اسم الفندق' },
        { value: 'waldorfAstoria', label: language === 'en' ? 'Waldorf Astoria' : 'والدروف ستوريا' },
        { value: 'fourSeasons', label: language === 'en' ? 'Four Seasons' : 'فورسيزون' },
        { value: 'alRayya', label: language === 'en' ? 'Al Rayya' : 'الرايه' },
        { value: 'symphony', label: language === 'en' ? 'The Symphony' : 'السيمفوني' },
        { value: 'jumeirah', label: language === 'en' ? 'Jumeirah' : 'الجميرا' },
        { value: 'crownPlaza', label: language === 'en' ? 'Crowne Plaza' : 'كراون بلازا' },
        { value: 'grandHyattResidence', label: language === 'en' ? 'Grand Hyatt Residence' : 'جراند حياة رزيدنس' },
        { value: 'grandHyatt360', label: language === 'en' ? 'Grand Hyatt 360' : 'جراند حياة 360' },
        { value: 'millenniumSalmiya', label: language === 'en' ? 'Millennium Salmiya' : 'ميلينيوم السالميه' },
        { value: 'millenniumCentral', label: language === 'en' ? 'Millennium Central' : 'ميلينيوم سنترال' },
        { value: 'hyattRegencyAlKout', label: language === 'en' ? 'Hyatt Regency Al Kout' : 'حياة ريجنسي الكوت' },
        { value: 'alKoutBeach', label: language === 'en' ? 'Al Kout Beach' : 'شاطيء الكوت' },
        { value: 'hiltonResort', label: language === 'en' ? 'Hilton Resort Al Mangaf' : 'منتجع هيلتون المنقف' },
        { value: 'regencyAlBidaa', label: language === 'en' ? 'Regency Al Bidaa' : 'ريجنسي البدع' },
        { value: 'movenpickHawraa', label: language === 'en' ? 'Movenpick Al Harraa' : 'موفمبيك الحره' },
        { value: 'movenpickBidaa', label: language === 'en' ? 'Movenpick Al Bidaa' : 'موفمبيك البدع' },
        { value: 'radissonBlu', label: language === 'en' ? 'Radisson Blu' : 'راديسون بلو' },
        { value: 'marina', label: language === 'en' ? 'Marina' : 'مارينا' },
        { value: 'gardenHotel', label: language === 'en' ? 'Garden Hotel' : 'جاردن هوتيل' },
        { value: 'arjaanBidaa', label: language === 'en' ? 'Arjaan Al Bidaa' : 'ارجان البدع' },
        { value: 'alHamra', label: language === 'en' ? 'Al Hamra' : 'الحمراء' },
        { value: 'safirFintas', label: language === 'en' ? 'Safir Al Fintas' : 'سفير الفنطاس' },
        { value: 'stRegisSheraton', label: language === 'en' ? 'St. Regis (Sheraton)' : 'سانت ريجس (الشيراتون)' },
        { value: 'holidayInnSalmiya', label: language === 'en' ? 'Holiday Inn Salmiya' : 'هوليدي ان السالميه' },
        { value: 'holidayInnFarwaniya', label: language === 'en' ? 'Holiday Inn Farwaniya' : 'هوليدي ان الفروانيه' },
        { value: 'copthorneJahra', label: language === 'en' ? 'Copthorne Jahra (Slailey)' : 'كوبثرون الجهراء (سليل)' },
        { value: 'alNakheel', label: language === 'en' ? 'Al Nakheel' : 'النخيل' },
        { value: 'yosifiHall', label: language === 'en' ? 'Yousifi Hall' : 'قاعه اليوسفي' },
        { value: 'divanHall', label: language === 'en' ? 'Divan Hall' : 'قاعه ديفان' },
        { value: 'boulevardHall', label: language === 'en' ? 'Boulevard Hall' : 'قاعه بوليفارد' },
        { value: 'ajwanHallGrandHotel', label: language === 'en' ? 'Ajwan Hall (Grand Hotel)' : 'قاعة اجوان (جراند هوتيل)' },
        { value: 'salwaSabahHall', label: language === 'en' ? 'Salwa Sabah Hall' : 'قاعة سلوى الصباح' },
        { value: 'royalHall', label: language === 'en' ? 'Royal Hall' : 'قاعة الملكيه' },
        { value: 'regencyAlKout', label: language === 'en' ? 'Regency Al Kout' : 'ريجنسي الكوت' },
        { value: 'ramada', label: language === 'en' ? 'Ramada' : 'رمادا' },
        { value: 'other', label: language === 'en' ? 'Other' : 'أخرى' },
    ];


    const fetchBookings = async () => {


        try {
            const query = new URLSearchParams();
            if (startDate) query.append('startDate', startDate.toISOString().split('T')[0]);
            if (endDate) query.append('endDate', endDate.toISOString().split('T')[0]);
            console.log(`https://peacesalonkw.com/api/admin/bookings?${query.toString()}`);
            const response = await fetch(`https://peacesalonkw.com/api/admin/bookings?${query.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch bookings');
            }

            const data = await response.json();
            setBookings(data.reverse());
        } catch (error) {
            console.error(error);
        }
    };

    const handleLogin = async () => {
        setError('');
        try {
            const response = await fetch('https://peacesalonkw.com/api/admin/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
            });

            if (!response.ok) {
                throw new Error('Invalid credentials');
            }

            const data = await response.json();
            setLoggedIn(true);
            setAdminId(data.adminId);
            localStorage.setItem('isLoggedIn', 'true');
            localStorage.setItem('adminId', data.adminId);
            alert(data.message);
        } catch (error) {
            setError(error.message);
        }
    };

    const handleLogout = async () => {
        await fetch('https://peacesalonkw.com/api/admin/logout', { method: 'POST' });
        setLoggedIn(false);
        setAdminId('');
        setBookings([]);
    };

    const handleBookingClick = (booking) => {
        setSelectedBooking(booking);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedBooking(null);
    };

    const downloadCSV = () => {
        const headers = [
            'Client Name',
            'Service Type',
            'Wedding Date',
            'Adults',
            'Children',
            'Bride',
            'Employee',
            'Mobile',
            'Notes',
            'Location',
            'Hotel Name',
            'Map Link',
        ];

        const rows = bookings.map((booking) => [
            booking.clientName,
            booking.serviceType,
            new Date(booking.eventDate).toLocaleDateString(),
            booking.adults,
            booking.children,
            booking.bride ? 'Yes' : 'No',
            booking.assignedStaff,
            booking.phoneNumber,
            booking.notes,
            booking.partyLocation,
            booking.hotelName,
            booking.mapLink,
        ]);

        const csvContent = [
            headers.join(','), // Add header row
            ...rows.map((row) => row.map((field) => `"${field || ''}"`).join(',')), // Escape fields and join rows
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `bookings_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        let interval;

        if (loggedIn) {
            fetchBookings();
            interval = setInterval(() => {
                fetchBookings();
            }, 10000);
        }
        return () => clearInterval(interval);
    }, [startDate, endDate, loggedIn]);

    if (!loggedIn) {
        return (
            <Container className="mt-5">
                <h2>Admin Login</h2>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField label="Username" variant="outlined" fullWidth margin="normal" value={username} onChange={(e) => setUsername(e.target.value)} />
                <TextField label="Password" type="password" variant="outlined" fullWidth margin="normal" value={password} onChange={(e) => setPassword(e.target.value)} />
                <Button variant="contained" color="primary" onClick={handleLogin}>
                    Login
                </Button>
            </Container>
        );
    }

    return (
        <Container className="mt-5">
            <h2>Admin Panel</h2>
            <Button variant="contained" color="secondary" onClick={handleLogout}>
                Logout
            </Button>
            <p className="mt-3">Welcome, Admin ID: {adminId}</p>
            <Grid container spacing={3} className="mt-4">
                <Grid item xs={3}>
                    <input
                        type="text"
                        placeholder="Search bookings..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-control"
                    />
                </Grid>
                <Grid item xs={2}>
                    <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Start Date"
                        className="form-control"
                    />
                </Grid>
                <Grid item xs={2}>
                    <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="End Date"
                        className="form-control"
                    />
                </Grid>
                <Grid item xs={2}>
                    <Button variant="contained" color="info" onClick={fetchBookings}>
                        Filter Data
                    </Button>
                </Grid>
                <Grid item xs={2}>
                    <Button variant="contained" color="success" onClick={downloadCSV}>
                        Download CSV
                    </Button>
                </Grid>
            </Grid>

            <TableContainer component={Paper} className="mt-4">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Client Name</TableCell>
                            <TableCell>Service Type</TableCell>
                            <TableCell>Wedding Date</TableCell>
                            <TableCell>Time</TableCell>
                            <TableCell>Adults</TableCell>
                            <TableCell>Children</TableCell>
                            <TableCell>Bride</TableCell>
                            <TableCell>Employee</TableCell>
                            <TableCell>Mobile</TableCell>
                            <TableCell>Additional Mobile</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Hotel Name</TableCell>
                            <TableCell>Street Number</TableCell>
                            <TableCell>Avenue Number</TableCell>
                            <TableCell>House Number</TableCell>
                            <TableCell>VIP Room</TableCell>
                            <TableCell>Language</TableCell>
                            <TableCell>Notes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredBookings.map((booking) => (
                            <TableRow key={booking._id} onClick={() => handleBookingClick(booking)} style={{ cursor: 'pointer' }}>
                                <TableCell>{booking.clientName}</TableCell>
                                <TableCell>
                                    {booking.serviceType
                                        .split(',')
                                        .map((type) => serviceTypeOptions.find(option => option.value === type.trim())?.label || type.trim())
                                        .join(', ')}
                                </TableCell>

                                <TableCell>{new Date(booking.eventDate).toLocaleDateString()}</TableCell>
                                <TableCell>{booking.time}</TableCell>
                                <TableCell>{booking.adults}</TableCell>
                                <TableCell>{booking.children}</TableCell>
                                <TableCell>{booking.bride ? 'Yes' : 'No'}</TableCell>
                                <TableCell>
                                    {booking.assignedStaff
                                        .split(',')
                                        .map((staff) => assignedStaffOptions.find(option => option.value === staff.trim())?.label || staff.trim())
                                        .join(', ')}
                                </TableCell>
                                <TableCell>{booking.phoneNumber}</TableCell>
                                <TableCell>{booking.additionalPhoneNumber}</TableCell>
                                <TableCell>
                                    {partyLocationOptions.find(option => option.value === booking.partyLocation)?.label || booking.partyLocation}
                                </TableCell>
                                <TableCell>
                                    {hotelOptions.find(option => option.value === booking.hotelName)?.label || booking.hotelName}
                                </TableCell>
                                <TableCell>{booking.streetNumber}</TableCell>
                                <TableCell>{booking.avenueNumber}</TableCell>
                                <TableCell>{booking.houseNumber}</TableCell>
                                <TableCell>{booking.vipRoom ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{booking.language}</TableCell>
                                <TableCell>{booking.notes}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Booking Details</DialogTitle>
                <DialogContent>
                    {selectedBooking && (
                        <div>
                            <p><strong>Client Name:</strong> {selectedBooking.clientName}</p>
                            <p><strong>Service Type:</strong> {selectedBooking.serviceType
                                .split(',')
                                .map((type) => serviceTypeOptions.find(option => option.value === type.trim())?.label || type.trim())
                                .join(', ')}</p>
                            <p><strong>Wedding Date:</strong> {new Date(selectedBooking.eventDate).toLocaleDateString()}</p>
                            <p><strong>Time:</strong> {selectedBooking.time}</p>
                            <p><strong>Adults:</strong> {selectedBooking.adults}</p>
                            <p><strong>Children:</strong> {selectedBooking.children}</p>
                            <p><strong>Bride:</strong> {selectedBooking.bride ? 'Yes' : 'No'}</p>
                            <p><strong>Employee:</strong> {selectedBooking.assignedStaff
                                .split(',')
                                .map((staff) => assignedStaffOptions.find(option => option.value === staff.trim())?.label || staff.trim())
                                .join(', ')}</p>
                            <p><strong>Mobile:</strong> {selectedBooking.phoneNumber}</p>
                            <p><strong>Additional Mobile:</strong> {selectedBooking.additionalPhoneNumber}</p>
                            <p><strong>Location:</strong> {partyLocationOptions.find(option => option.value === selectedBooking.partyLocation)?.label || selectedBooking.partyLocation}</p>
                            <p><strong>Hotel Name:</strong> {hotelOptions.find(option => option.value === selectedBooking.hotelName)?.label || selectedBooking.hotelName}</p>
                            <p><strong>Street Number:</strong> {selectedBooking.streetNumber}</p>
                            <p><strong>Avenue Number:</strong> {selectedBooking.avenueNumber}</p>
                            <p><strong>House Number:</strong> {selectedBooking.houseNumber}</p>
                            <p><strong>VIP Room:</strong> {selectedBooking.vipRoom ? 'Yes' : 'No'}</p>
                            <p><strong>Language:</strong> {selectedBooking.language}</p>
                            <p><strong>Notes:</strong> {selectedBooking.notes}</p>
                            <p><strong>Map Link:</strong> <a href={selectedBooking.mapLink} target="_blank" rel="noopener noreferrer">{selectedBooking.mapLink}</a></p>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default AdminPanel;